<template>
  <div :class="[$style.notification, isRead ? $style.notification__read : $style[`notification__${type}`]]">
    <NuxtLink
      v-if="link !== ''"
      :to="link"
      :class="$style.label"
      @click="emit('processed-cancel-order', id)"
    >
      <div :class="$style.body">
        <Icon
          :name="iconSelected(icon)"
          :size="25"
          :fill="iconColor"
          :class="{
            [$style.icon]: true,
            [$style[`icon__${type}`]]: true,
          }"
        />
        <div>
          <Heading4 :class="$style[`label__${type}`]">
            <span>{{ label }}</span>
          </Heading4>

          <Paragraph5 :class="[$style.description, length > 1 ? $style.description__link : '']">
            <span v-if="length === 1">{{ orderNumber }}</span>
            <span
              v-if="length === 1"
              :class="$style.description__link"
            >
              {{ $t('notification.message.more') }}
            </span>
            <span v-else>{{ message }}</span>
          </Paragraph5>
          <slot />
        </div>
      </div>
    </NuxtLink>
    <div
      v-else
      :class="$style.label"
      @click="emit('processed-cancel-order', id)"
    >
      <div :class="$style.body">
        <Icon
          :name="iconSelected(icon)"
          :size="25"
          :fill="iconColor"
          :class="{
            [$style.icon]: true,
            [$style[`icon__${type}`]]: true,
          }"
        />
        <div>
          <Heading4 :class="$style[`label__${type}`]">
            <span>{{ label }}</span>
          </Heading4>

          <Paragraph5 :class="$style.description">
            <span>{{ orderNumber }}</span>
            <span :class="$style.description__link">{{ message }}</span>
          </Paragraph5>
          <slot />
        </div>
      </div>
    </div>
    <div
      v-if="needClose"
      :class="$style.containerClose"
      @click="emit('dismiss', id)"
    >
      <IconCross
        :class="$style.close"
        :size="15"
        fill="var(--color-mono-700)"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { Heading4, Paragraph5 } from '~/components-daisy/typography';
import Icon from '~/components-daisy/icons/Icon.vue';
import IconCross from '~/components-daisy/icons/icons/cross/IconX.vue';

type Props = {
  type?: string;
  icon: string;
  label: string;
  message: string;
  needClose?: boolean;
  link: string;
  id: string;
  isRead?: boolean;
  length?: number;
  orderNumber?: string;
};
const props = withDefaults(defineProps<Props>(), {
  type: 'white',
  needClose: true,
  isRead: true,
  length: 0,
  orderNumber: '',
});

const iconColor = computed(() => {
  let color: any;
  if (props.icon === 'order') {
    color = 'var(--color-blue-800)';
  }
  if (props.icon === 'transmission') {
    color = 'var(--color-yellow-800)';
  }
  if (props.icon === 'modification') {
    color = 'var(--color-purple-800)';
  }
  if (props.icon === 'review') {
    color = 'var(--color-review-500)';
  }
  if (props.icon === 'cancellation') {
    color = 'var(--color-red-800)';
  }
  if (props.icon === 'refused') {
    color = 'var(--color-red-800)';
  }
  if (props.icon === 'expired') {
    color = 'var(--color-mono-700)';
  }
  return color;
});

const iconSelected = (value: any) => {
  let icon: any;
  if (value === 'order') {
    icon = 'IconCombination';
  } else if (value === 'transmission') {
    icon = 'IconRocket';
  } else if (value === 'review') {
    icon = 'IconPin';
  } else if (value === 'modification') {
    icon = 'IconPencil';
  } else if (value === 'cancellation') {
    icon = 'IconRoundX';
  } else if (value === 'refused') {
    icon = 'IconX';
  } else if (value === 'expired') {
    icon = 'IconClock';
  }
  return icon;
};
const emit = defineEmits(['dismiss', 'processed-cancel-order']);
</script>

<style lang="postcss" module>
.notification {
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  justify-content: space-between;
  padding-right: var(--space-100);
  margin: var(--space-100) var(--space-50);
  border-radius: var(--border-radius-200);
  box-shadow: var(--shadow-200) !important;
  gap: var(--space-300);
  height: 88px;

  &__cancellation {
    background-color: var(--color-red-100);
    border: 1px solid var(--color-red-800);
  }
  &__refused {
    background-color: var(--color-red-100);
    border: 1px solid var(--color-red-800);
  }
  &__modification {
    background-color: var(--color-purple-200);
    border: 1px solid var(--color-purple-800);
  }
  &__order {
    background-color: var(--color-blue-200);
    border: 1px solid var(--color-blue-800);
  }
  &__read {
    background-color: var(--color-mono-100);
    border: 1px solid var(--color-mono-100);
  }
  &__review {
    background-color: var(--color-review-200);
    border: 1px solid var(--color-review-800);
  }
  &__transmission {
    background-color: var(--color-yellow-200);
    border: 1px solid var(--color-yellow-800);
  }
  &__expired {
    background-color: var(--color-mono-300);
    border: 1px solid var(--color-mono-700);
  }

  /* Needed for the toast notification */
  &:not(:last-child) {
    margin-bottom: var(--space-100);
  }

  .body {
    display: flex;
    flex-wrap: nowrap;
    flex-direction: row;
    justify-items: center;
    justify-content: flex-start;
    align-items: center;
    gap: var(--space-100);
    cursor: pointer;
    .description {
      color: var(--color-mono-800);
      line-height: 30px;
      width: 100%;
      display: flex;
      gap: var(--space-50);
      &__link {
        text-decoration: underline;
      }
    }

    .label {
      color: var(--color-mono-700);
      font-weight: var(--font-600-weight);
      line-height: 30px;
      text-decoration: none;
      cursor: pointer;
      &__cancellation {
        color: var(--color-red-800);
      }
      &__refused {
        color: var(--color-red-800);
      }
      &__read {
        color: var(--color-mono-800);
      }
      &__review {
        color: var(--color-review-500);
      }
      &__modification {
        color: var(--color-purple-800);
      }
      &__transmission {
        color: var(--color-yellow-800);
      }
      &__order {
        color: var(--color-blue-800);
      }
      &__expired {
        color: var(--color-mono-700);
      }
    }
  }
  .close {
    cursor: pointer;
    width: 42px;
    z-index: 40;
  }
}

.notification__read {
  background-color: none;
  box-shadow: none;
}

.icon {
  height: 85px;
  border-radius: var(--border-radius-200) 0 0 var(--border-radius-200);
  width: 40px;
  padding: 0 var(--space-50) 0 var(--space-100);

  &__read {
    background-color: none;
  }
  &__review {
    background-color: #f6f6e8;
  }
  &__cancellation {
    background-color: var(--color-red-100);
  }
  &__refused {
    background-color: var(--color-red-100);
  }
  &__modification {
    background-color: #f1ebf9;
  }
  &__transmission {
    background-color: var(--color-yellow-200);
  }
  &__order {
    background-color: var(--color-blue-200);
  }
  &__expired {
    background-color: var(--color-mono-300);
  }
}

.containerClose {
  height: 42px;
  width: 42px;
  display: flex;
  justify-content: center;
  align-items: center;
  transition: all 200ms;
  cursor: pointer;
  border-radius: var(--border-radius-200);

  &:hover {
    background-color: var(--color-mono-100);
    box-shadow: var(--shadow-200);
    transition: all 200ms;
  }
}
</style>
